/* Core & libraries */
import React, {useEffect, useState} from "react";
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import csvDownload from "json-to-csv-export";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch }  from "../endpoints/base";
import {
  DELETE_PEOPLE_ENDPOINT,
  PEOPLE_GRID_ENDPOINT,
  SEARCH_PEOPLE_ENDPOINT,
  INVITATION_SHAREDLINK_ENDPOINT,
  INVITATION_ENDPOINT,
  PENDING_INVITATION_ENDPOINT,
  EXPIRED_INVITATION_ENDPOINT,
  PENDING_INVITATION_COUNT_ENDPOINT,
  PENDING_INVITATION_RESEND_ENDPOINT,
  PENDING_INVITATION_REVOKE_ENDPOINT,
  TRAVELER_DETAILS_UPDATE_ENDPOINT,
  PEOPLE_GRID_PERMISION_ENDPOINT,
  GET_RECENT_MILES_USERS,
  PEOPLE_COUNT_ENDPOINT,
  SEARCH_PEOPLE_STORED_VALUE_ENDPOINT,
  UPDATE_EMAIL_ENDPOINT,
  RESEND_EMAIL_CODE_ENDPOINT,
  UPDATE_EMAIL_VERIFICATION_ENDPOINT,
  SEND_EMAIL_VERIFICATION_CODE,
  EDIT_USER_CITIPCAN_ENDPOINT,
  UPDATE_COMPAMY_CITIPCAN_ENDPOINT,
  DOWNLOAD_TEAMS_GRID_DATA_ENPOINT,
  DOWNLOAD_REMOVED_PEOPLE_GRID_DATA_ENPOINT,
  GET_OPTIONS_ENDPOINT,
} from '../endpoints/endpoints';
import { useNotificationContext } from "../contexts/notificationContext";
import { useUserContext } from "../contexts/userContext";
import { useLocaleContext } from "../contexts/localeContext";
import { roles } from "../constants/appConstants";
import { getHeadersForCsv } from "helpers/general";


/* Components */
/* Styles */
const messages = defineMessages({
  personalCard: {
    id: 'teamsGrid.personalCard',
  },
});

export const usePeopleService = () => {
  const [peopleResponse, setPeopleResponse] = useState({})
  const { genericApiFetch } = useGenericApiFetch();
  const [pendingInviteResponse, setPendingInviteResponse] = useState({})
  const [invitationCount, setInvitationCount] = useState("")
  const [expiryDaysCount, setExpiryDaysCount] = useState('');
  const [isPendingFetching, setIsPendingFetching] = useState(false)
  const intl = useIntl();

  const { setNotification } = useNotificationContext();
  const userDetails = useUserContext();
  const locale = useLocaleContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;


  const getPeopleList = async (options, type) => {
    if (options) {
      let filters = "";
      if (options.filters) {
        for (const [key, value] of Object.entries(options.filters)) {
          value.map(item => {
            if (item) {
              filters += `${filters ? "," : ""}${key}-${item}`
            }
          })
        }
      }
      options = {
        ...options,
        filters
      }
      
      const response = await genericApiFetch({
        url: PEOPLE_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{status}", type),
        method: "GET",
        queryParams: options,
        spinner: type === "ACTIVE" ? "peopleGridSpinner" : "removedPeopleGridSpinner"
      });

      if (response.success) {
        setPeopleResponse(response.data);
      } else {
        setPeopleResponse({ results: null });
      }
    }
  };

  const getRecentMilesUsers = async () => {
    const response = await genericApiFetch({
      url: GET_RECENT_MILES_USERS.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET"
    })

    if (response.success) {
      return response.data;
    }
    return false
  };  

  const getSharedLink = async (teamId = null, spinner="invitePeopleButton") => {
    let queryParams = {};
    if (teamId) {
      queryParams = {
        groupid: teamId
      };
    }
    const response = await genericApiFetch({
      url: INVITATION_SHAREDLINK_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
      spinner: spinner,
      queryParams,
    })

    if (response.success) {
      return response.data;
    }
    return false
  };

  const sendInvite = async (inviteData) => {
    const response = await genericApiFetch({
      url: INVITATION_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data : {
        ...inviteData,
        locale: locale
      },
      spinner: "sendInviteSpinner"
    })
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.invitation.successTitle" />,
        <FormattedMessage
          id="notifications.invitation.successText"
          values={{ count: inviteData?.emailAddresses?.length }}
        />
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.invitation.errorTitle" />,
      <FormattedMessage id="notifications.invitation.errorText" />
    )
    return false
  };

  const deletePeople = async (entity) => {
    const response = await genericApiFetch({
      url: DELETE_PEOPLE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userId}", entity.entityID),
      method: "DELETE",
      spinner: "deleteActionSpinner",
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.deleteSuccessTitle" />,
        <FormattedMessage id="notification.deleteSuccessText" values={{name: `${entity?.firstName} ${entity?.lastName}`}}/>
      )
      getUsersCount("REMOVED")
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="notification.deleteErrorTitle" />,
      <FormattedMessage id="notification.deleteErrorText" />
    )
    return false;
  }

  const searchPeople = async (searchTerm, status) => {
    const response = await genericApiFetch({
      url: SEARCH_PEOPLE_ENDPOINT.replace("{companyId}", companyEntityId).replace("{status}", status).replace("{searchTerm}", searchTerm),
      method: "GET",
      spinner: "searchGridFilters"
    });
    if (response.success) {
      if (response?.data?.users) {      
        return response.data.users;
      } else return []
    }
    return false;
  }

  const searchPeopleStoredValue = async (searchTerm, groupEntityId) => {
    const payload = {
      searchTerm: searchTerm,
      status: "ACTIVE",
      groupEntityId: groupEntityId
    }
    const response = await genericApiFetch({
      url: SEARCH_PEOPLE_STORED_VALUE_ENDPOINT,
      method: "POST",
      data: payload,
      spinner: "searchGridFilters"
    });

    if (response.success) {
      if (response?.data?.users) {      
        return response.data.users;
      } else return []
    }
    return false;
  };

  const getPendingInviteList = async (options) => {
    if (options && !isPendingFetching) {
      setIsPendingFetching(true);
      const { status, ...queryParams } = options;
      const url = status === 'EXPIRED'
        ? EXPIRED_INVITATION_ENDPOINT.replace("{companyEntityId}", companyEntityId)
        : PENDING_INVITATION_ENDPOINT.replace("{companyEntityId}", companyEntityId);
      const response = await genericApiFetch({
        url,
        method: "GET",
        queryParams
      });
      if (response.success) {
        setPendingInviteResponse(response.data);
        setIsPendingFetching(false);
      } else {
        setIsPendingFetching(false);
      }
    }
  };

  const getPendingInviteCount = async () => {
    const countResponse = await  genericApiFetch({
      url: PENDING_INVITATION_COUNT_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
    })
    if (countResponse.success) {
      setInvitationCount(countResponse.data.invitationCount);
      setExpiryDaysCount(countResponse.data?.expiryDays);
    }
  };

  const getRoleOptions = async (options) => {
    const response = await genericApiFetch({
      url: GET_OPTIONS_ENDPOINT,
      queryParams: {
        optionName: options,
      },
      method: 'GET',
    });

    if (response.success) {
      return response?.data?.options;
    }
    setNotification(
      'error',
      <FormattedMessage id="notifications.genericTitle" />,
      <FormattedMessage id="notifications.genericDescription" />,
    );
    return false;
  };

  const resendInvite = async (reqBody, singleResend, invitationCount) => {
    const response = await  genericApiFetch({
      url: PENDING_INVITATION_RESEND_ENDPOINT,
      method: "POST",
      data: reqBody,
      spinner: singleResend ? `resendInviteSpinner.${reqBody.invitationIds[0]}` : 'resendInviteSpinner.multiple',
    })
    const singleNotification = true;
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.resendSuccessTitle" />,
        reqBody?.invitationIds?.length === 1 ? 
        <FormattedMessage id="notifications.resendSuccessText" />
          : 
        <FormattedMessage id="notifications.resendSuccessText.multiple" values={{multipleInvitaitons: reqBody.allInvitations ? invitationCount :reqBody.invitationIds.length}}/>,
        singleNotification
      )
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.revokeErrorTitle" />,
        <FormattedMessage id="notifications.revokeErrorText" />,
        singleNotification
      )
      return false
    }
  };

  const revokeInvite = async (invitation) => {
    const response = await  genericApiFetch({
      url: PENDING_INVITATION_REVOKE_ENDPOINT,
      method: "POST",
      data: {
        invitationId: invitation.invitationID
      },
      spinner: `revokeInviteSpinner.${invitation.invitationID}`
    })
    const singleNotification = true;
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.revokeSuccessTitle" />,
        <FormattedMessage id="notifications.revokeSuccessText" />,
        singleNotification
      )
      return true
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />,
      singleNotification
    )
    return false

  };

  const updatePeople = async (payload) => {
    const {userId, ...data} = payload;
    const response = await  genericApiFetch({
      url: TRAVELER_DETAILS_UPDATE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userEntityId}", userId),
      method: "PUT",
      data: data,
      spinner: "updatePeopleSpinner"
    })
    if (response.success) {
      if (data.firstName) {
        setNotification(
          "success",
          <FormattedMessage id="notifications.roleChangedTitle" />,
          <FormattedMessage
            id="notifications.roleChangedDescription"
            values={{
              name: `${data?.firstName} ${data?.lastName}`,
              role: (data.roleId) === roles.travelManegerRole ?
                  <FormattedMessage id={`roles.8.${providerCode}`} /> :
                  <FormattedMessage id={`roles.7.${providerCode}`} />
          }}
          />
        )
      } else {
        setNotification(
          "success",
          <FormattedMessage id="notifications.updateMemberSuccessTitle" />,
          <FormattedMessage id="notifications.updateMemberSuccessText" />
        )
      }
      return true
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.updateMemberErrorTitle" />,
      <FormattedMessage id="notifications.updateMemberErrorText" />
    )
    return false

  };

  const leaveCompany = async (entity) => {
    const response = await genericApiFetch({
      url: TRAVELER_DETAILS_UPDATE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userEntityId}", entity.entityID),
      method: "DELETE",
      spinner: "leaveCompanySpinner",
    });
    if (response.success) {      
      return response;
    }
    setNotification(
      "error",
      <FormattedMessage id="notification.deleteErrorTitle" />,
      <FormattedMessage id="notification.deleteErrorText" />
    )
    return false;
  }

  const gridActionPermision = async (entity, spinner) => {
    const response = await genericApiFetch({
      url: PEOPLE_GRID_PERMISION_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userEntityId}", entity),
      method: "GET",
      spinner: spinner || "checkPermissionSpinner",
    });
    if (response.success) {
      return response.data
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.permissionErrorTitle" />,
      <FormattedMessage id="notifications.permissionErrorDescription" />
    )
    return false;
  }

  const getUsersCount = async (status) => {
    const countResponse = await  genericApiFetch({
      url: PEOPLE_COUNT_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{status}", status),
      method: "GET",
    })
    if (countResponse.success) {
      return countResponse?.data?.userByStatusCount;
    }
  };

  const updateUserEmail = async (payload, emailVerification) => {
    const {userId, ...data} = payload;
    const response = await  genericApiFetch({
      url: UPDATE_EMAIL_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userEntityId}", userId),
      method: "PUT",
      data: data,
      spinner: "updateEmailSpinner"
    })
    if (response.success) {
      if ( !emailVerification && response.data.status === "SUCCESS") {

      }
      return response.data
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.updateEmailErrorTitle" />,
      <FormattedMessage id="notifications.updateEmailErrorText" />
    )
    return false
  };


  const resendEmailCode = async () => {
    const response = await genericApiFetch({
      url: RESEND_EMAIL_CODE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()),
      method: 'GET',
    });
    if (response.success) {
      setNotification(
        'success',
        <FormattedMessage id='updateEmailModal.resendNotificationTitle' />,
        <FormattedMessage id='updateEmailModal.resendNotificationText' />
      );
      return true;
    }
    setNotification(
      'danger',
      <FormattedMessage id='notifications.updateEmailErrorTitle' />,
      <FormattedMessage id='notifications.updateEmailErrorText' />
    );
    return false;
  };
  const updateUserEmailVerification = async (payload, emailVerification) => {
    const {userId, ...data} = payload;
    const response = await  genericApiFetch({
      url: UPDATE_EMAIL_VERIFICATION_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userEntityId}", userId),
      method: "POST",
      data: data,
      spinner: "updateUserEmailVerificationSpinner"
    })
    if (response.success) {
      return response.data
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.updateEmailErrorTitle" />,
      <FormattedMessage id="notifications.updateEmailErrorText" />
    )
    return false
  };
  const sendEmailVerificationCode = async (payload) => {
    const {userId, ...data} = payload;
    const response = await  genericApiFetch({
      url: SEND_EMAIL_VERIFICATION_CODE.replace("{providerCode}", providerCode?.toLowerCase()),
      method: "POST",
      data: data,
    })
    if (response.success) {
      return response.data
    }
    return false
  };

  const editUserPCANs = async (payload, userId) => {
    const response = await  genericApiFetch({
      url: EDIT_USER_CITIPCAN_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userEntityId}", userId),
      method: "PUT",
      data: payload,
      spinner: "editUserPCANSpinner"
    })
    if (response.success) {
      if(response.data.status === "SUCCESS") {
        setNotification(
          "success",
          <FormattedMessage id="notifications.editUserPCANsSuccesTitle" />,
          <FormattedMessage id="notifications.editUserPCANsSuccesText" />
        )
      }
      return response.data
    }
    setNotification(
      "error",
      <FormattedMessage id="notifications.editUserPCANsErrorTitle" />,
      <FormattedMessage id="notifications.editUserPCANsErrorErrorText" />
    )
    return false
  };

  const downloadTeamsGridData =  async () => {
    const response = await  genericApiFetch({
      url: DOWNLOAD_TEAMS_GRID_DATA_ENPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      spinner: "downloadTeamsGridData",
    })
    if (response.success) {
      const customCsvData = response.data.items.map((item) => {
        return {
          ...item,
          paymentCards: item?.paymentCards?.length
            ? `**** ${item.paymentCards[0]?.fopLastFour || ''}`
            : intl.formatMessage(messages.personalCard),
        };
      });
      csvDownload({
        data: customCsvData,
        filename: response.data.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data?.items, "teamsGrid", intl)
      });
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };

  const downloadPeopleGridData =  async (status) => {
    const response = await  genericApiFetch({
      url: DOWNLOAD_REMOVED_PEOPLE_GRID_DATA_ENPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId).replace("{status}", status),
      method: "GET",
      spinner: status === "REMOVED" ? "downloadRemovedPeopleGridData" : "downloadActivePeopleGridData",
    })
    if (response.success) {
      csvDownload({
        data: response.data.items,
        filename: response.data.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data?.items, status === "REMOVED" ? "removedPeopleGrid": "peopleGrid", intl)
      });
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };


  return {
    peopleResponse,
    pendingInviteResponse,
    invitationCount,
    isPendingFetching,
    getPeopleList,
    getSharedLink,
    sendInvite,
    deletePeople,
    searchPeople,
    getPendingInviteList,
    getPendingInviteCount,
    resendInvite,
    revokeInvite,
    updatePeople,
    leaveCompany,
    gridActionPermision,
    getRecentMilesUsers,
    getUsersCount,
    searchPeopleStoredValue,
    updateUserEmail,
    resendEmailCode,
    updateUserEmailVerification,
    sendEmailVerificationCode,
    editUserPCANs,
    downloadTeamsGridData,
    downloadPeopleGridData,
    getRoleOptions,
    expiryDaysCount,
  };
}

